import type { Place, Schedule } from "../../types";
import { useEdition } from "../../hooks/useEdition";
import { useTranslations } from "../../hooks/useTranslations";
import { getLocalizedName } from "../../lib/localization";
import { EmptyState } from "../ui/EmptyState";
import { ScheduleItem } from "./ScheduleItem";

type ScheduleByPlaceProps = {
  schedules: Schedule[];
  onArtistClick: (artistId: string, elementId?: string) => void;
  onEventClick: (eventId: string, elementId?: string) => void;
};

export function ScheduleByPlace({
  schedules,
  onArtistClick,
  onEventClick,
}: ScheduleByPlaceProps) {
  const { locale, edition } = useEdition();
  const t = useTranslations();
  const fallback = edition?.default_locale_code;

  // Group schedules by place
  const placeMap = new Map<string, { place: Place; schedules: Schedule[] }>();

  for (const schedule of schedules) {
    if (!schedule.place) continue;

    const existing = placeMap.get(schedule.place._id);
    if (existing) {
      existing.schedules.push(schedule);
    } else {
      placeMap.set(schedule.place._id, {
        place: schedule.place as Place,
        schedules: [schedule],
      });
    }
  }

  // Sort places by order
  const places = Array.from(placeMap.values()).sort(
    (a, b) => (a.place.order ?? 0) - (b.place.order ?? 0),
  );

  // Sort schedules within each place by time
  for (const placeData of places) {
    placeData.schedules.sort((a, b) => {
      return `${a.start_date}T${a.start_time ?? ""}`.localeCompare(
        `${b.start_date}T${b.start_time ?? ""}`,
      );
    });
  }

  if (places.length === 0) {
    return <EmptyState message={t.noSchedulesForDay} />;
  }

  return (
    <div className="festapp-schedule-by-place">
      {places.map(({ place, schedules: placeSchedules }) => (
        <div key={place._id} className="festapp-schedule-place-column">
          <h3 className="festapp-schedule-place-name">
            {getLocalizedName(place, locale, fallback) ?? place.name}
          </h3>
          <div className="festapp-schedule-place-items">
            {placeSchedules.map((schedule) => (
              <ScheduleItem
                key={schedule._id}
                schedule={schedule}
                itemId={schedule._id}
                onArtistClick={(artistId) => onArtistClick(artistId, schedule._id)}
                onEventClick={(eventId) => onEventClick(eventId, schedule._id)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
